<template>
  <v-container
    px-12
    :class="[$route.name === 'HistoryEvent' && $style.eventContainer]"
    :style="{
      overflowY: 'auto',
      maxHeight: $route.name === 'HistoryEvent' ? '100%' : `${tableHeight}px`
    }"
  >
    <v-row>
      <v-col>
        <span class="text-overline pb-2">Överblick</span>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card tile class="pt-8 pb-12">
          <TrafficMessageSubjectText />
          <TrafficMessageRoutesText />

          <template v-if="consequence">
            <TrafficMessageConsequenceText />
            <v-divider class="mt-6" />
          </template>
          <TrafficMessageForecastText />
          <v-divider />
          <template v-if="Object.values(trainDeviations).length">
            <TrafficMessageAffectedTrainsText />
            <v-divider class="mt-6" />
          </template>

          <template v-if="Object.values(delays).length">
            <TrafficMessageDelayTrainsText />
            <v-divider class="mt-6" />
          </template>
          <template v-if="Object.values(turnarounds).length">
            <TrafficMessageTurnaroundTrainsText />
            <v-divider class="mt-6" />
          </template>
          <template
            v-if="
              Object.values(replacements).length || model.replacement_comment
            "
          >
            <TrafficMessageReplacementText />
            <v-divider class="mt-6" />
          </template>
          <template
            v-if="
              Object.values(yellowVests).length || model.yellow_vest_comment
            "
          >
            <TrafficMessageYellowVestText />
            <v-divider class="mt-6" />
          </template>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="!isHistoryEvent">
      <v-col class="d-flex justify-end">
        <v-btn
          rounded
          color="primary"
          @click="sendTrafficMessage"
          :loading="sendingMessage"
        >
          <v-icon small class="pr-4">mdi-send</v-icon>
          Skicka meddelande
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="event && event.last_traffic_message && !isHistoryEvent">
      <v-col class="d-flex justify-end">
        <span
          ><span class="text-subtitle-2 text--secondary font-weight-bold"
            >Senast skickat:</span
          >
          {{ event.last_traffic_message.created | dateTime }}</span
        >
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TrafficMessageSubjectText from './TrafficMessagePreview/TrafficMessageSubjectText'
import TrafficMessageForecastText from './TrafficMessagePreview/TrafficMessageForecastText'
import TrafficMessageRoutesText from './TrafficMessagePreview/TrafficMessageRoutesText'
import TrafficMessageAffectedTrainsText from './TrafficMessagePreview/TrafficMessageAffectedTrainsText'
import TrafficMessageDelayTrainsText from './TrafficMessagePreview/TrafficMessageDelayTrainsText'
import TrafficMessageReplacementText from './TrafficMessagePreview/TrafficMessageReplacementText'
import TrafficMessageYellowVestText from './TrafficMessagePreview/TrafficMessageYellowVestText'
import TrafficMessageTurnaroundTrainsText from './TrafficMessagePreview/TrafficMessageTurnaroundTrainsText'
import TrafficMessageConsequenceText from './TrafficMessagePreview/TrafficMessageConsequenceText'
import inputTableHeight from '@/mixins/inputTableHeight'
import { get } from 'vuex-pathify'
import gql from 'graphql-tag'
import { format } from 'date-fns'

export default {
  name: 'TrafficMessagePreview',

  components: {
    TrafficMessageSubjectText,
    TrafficMessageForecastText,
    TrafficMessageRoutesText,
    TrafficMessageAffectedTrainsText,
    TrafficMessageReplacementText,
    TrafficMessageYellowVestText,
    TrafficMessageConsequenceText,
    TrafficMessageDelayTrainsText,
    TrafficMessageTurnaroundTrainsText
  },

  filters: {
    dateTime(date) {
      return format(new Date(date), 'yyyy-MM-dd HH:mm')
    }
  },

  data: () => ({
    sendingMessage: false
  }),

  computed: {
    selectedEventUUID: get('event/selectedUUID'),
    event: get('event/selectedEvent'),
    model: get('trafficMessage/model'),
    trainDeviations: get('trainDeviation/data'),
    delays: get('delay/data'),
    turnarounds: get('turnaround/data'),
    replacements: get('replacement/data'),
    yellowVests: get('yellowVest/data'),
    isHistoryEvent: get('history/isHistoryEvent'),
    consequence: get('trafficMessage/model@consequence')
  },

  mixins: [inputTableHeight(334)],

  methods: {
    async sendTrafficMessage() {
      this.sendingMessage = true
      this.$store.set('trafficMessage/model@event_uuid', this.event.uuid)
      this.$store.set(
        'trafficMessage/model@routes',
        (this.event?.routes || []).map(x => x.uuid)
      )
      this.$store.set('trafficMessage/model@subject', this.event.title)

      const mapCheckedUUIDs = array =>
        Object.values(array)
          .filter(x => x.checked && !x.deleted)
          .map(x => x.uuid)

      const payload = {
        ...this.model,
        train_deviations: mapCheckedUUIDs(this.trainDeviations),
        delays: mapCheckedUUIDs(this.delays),
        turnarounds: mapCheckedUUIDs(this.turnarounds),
        replacements: mapCheckedUUIDs(this.replacements),
        yellow_vests: mapCheckedUUIDs(this.yellowVests)
      }

      delete payload.created
      delete payload.uuid
      delete payload.__typename

      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: SendTrafficMessageInput!) {
            sendTrafficMessage(payload: $payload) {
              traffic_message_uuid
            }
          }
        `,
        variables: {
          payload
        }
      })
      this.$store.set(
        `event/data@${this.selectedEventUUID}.last_traffic_message`,
        {
          ...(this.event?.last_traffic_message || {}),
          created: new Date(),
          forecast_comment: this.model.forecast_comment,
          replacement_comment: this.model.replacement_comment,
          yellow_vest_comment: this.model.yellow_vest_comment,
          consequence: this.model.consequence
        }
      )

      this.$store.set('trafficMessage/model', payload)
      this.sendingMessage = false
    }
  }
}
</script>

<style lang="scss" module>
.eventContainer {
  height: calc(100vh - 176px);
}
</style>
